import React from "react";
import { motion } from "framer-motion";
import { AiOutlineMail } from "react-icons/ai";
import { COLORS } from "../constants/colors";

const PrivacyPolicy = () => {
  const currentDate = new Date().toLocaleDateString();

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center py-16 px-6"
      style={{ backgroundColor: COLORS.light }}
    >
      {/* Hero Section */}
      <motion.div
        className="w-full py-20 flex flex-col items-center justify-center text-center rounded-3xl shadow-lg mb-12"
        style={{
          background: COLORS.gradientHero,
          color: COLORS.textPrimary,
          maxWidth: "1100px",
        }}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h1
          className="text-5xl font-bold mb-3"
          style={{ color: COLORS.primaryDark }}
        >
          Privacy Policy
        </h1>
        <p className="text-base" style={{ color: COLORS.textSecondary }}>
          Last updated: <strong>{currentDate}</strong>
        </p>
      </motion.div>

      {/* Main Content Card */}
      <motion.div
        className="w-full max-w-5xl bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-200"
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <div className="p-8 md:p-12 space-y-12">

          {/* Introduction */}
          <section>
            <h2
              className="text-3xl font-semibold mb-3"
              style={{ color: COLORS.primaryDark }}
            >
              Introduction
            </h2>
            <p className="text-lg leading-relaxed" style={{ color: COLORS.textSecondary }}>
              Welcome to <strong>Gurukul ERP</strong>. This Privacy Policy explains how we collect,
              use, disclose, and safeguard your information when you use our platform. Please read
              this policy carefully. If you do not agree with these terms, kindly refrain from using
              our services.
            </p>
          </section>

          {/* Information We Collect */}
          <section
            className="p-6 rounded-2xl shadow-sm"
            style={{ backgroundColor: COLORS.primaryLight }}
          >
            <h2
              className="text-2xl font-semibold mb-3"
              style={{ color: COLORS.primaryDark }}
            >
              Information We Collect
            </h2>
            <p className="text-gray-700 mb-4">
              We may collect information about you in the following ways:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>
                <strong>Personal Data:</strong> Such as your name, email address, and contact details,
                provided when you register with Gurukul ERP.
              </li>
              <li>
                <strong>Usage Data:</strong> Automatically collected information about your activity,
                including pages visited and session duration.
              </li>
            </ul>
          </section>

          {/* Use of Information */}
          <section>
            <h2
              className="text-2xl font-semibold mb-3"
              style={{ color: COLORS.primaryDark }}
            >
              Use of Information
            </h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>To provide and maintain our services efficiently.</li>
              <li>To enhance user experience and platform functionality.</li>
              <li>To notify you about updates or important changes.</li>
              <li>To ensure security and prevent unauthorized access.</li>
              <li>To offer customer support and resolve technical issues.</li>
            </ul>
          </section>

          {/* Sharing of Information */}
          <section
            className="p-6 rounded-2xl shadow-sm"
            style={{ backgroundColor: COLORS.primaryLight }}
          >
            <h2
              className="text-2xl font-semibold mb-3"
              style={{ color: COLORS.primaryDark }}
            >
              Sharing of Information
            </h2>
            <p className="text-gray-700 mb-4">
              We do not share your personal information with third parties except in the following
              situations:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>With your explicit consent.</li>
              <li>To comply with legal or regulatory requirements.</li>
              <li>To protect our rights, privacy, and property.</li>
              <li>
                To investigate or prevent potential security or policy violations.
              </li>
            </ul>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2
              className="text-2xl font-semibold mb-3"
              style={{ color: COLORS.primaryDark }}
            >
              Changes to This Privacy Policy
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We may update our Privacy Policy from time to time. Updates will be posted on this
              page with a revised date. You are encouraged to review this page regularly to stay
              informed about any changes.
            </p>
          </section>

          {/* Contact Section */}
          <motion.section
            className="text-center p-8 rounded-3xl shadow-md"
            style={{ backgroundColor: COLORS.primaryLight }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h2
              className="text-2xl font-semibold mb-3"
              style={{ color: COLORS.primaryDark }}
            >
              Contact Us
            </h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              If you have any questions about this Privacy Policy, please reach out to us.
            </p>
            <motion.a
              href="mailto:info@gururkulerp.in"
              className="inline-flex items-center font-medium px-8 py-3 rounded-full shadow-md hover:shadow-lg transition duration-300"
              style={{
                backgroundColor: COLORS.primary,
                color: COLORS.white,
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
            >
              <a href="/contact" className="flex items-center">
                <AiOutlineMail className="mr-2 text-xl" />
                Contact Us
              </a>
            </motion.a>
          </motion.section>
        </div>
      </motion.div>
    </div>
  );
};
export default PrivacyPolicy;
